import type {ProductColorFragment} from '@/modules/commerce/product-color/query';
import type {ProductSizeFragment} from '@/modules/commerce/product-size/query';

import type {ClothingVariantCoreFragment} from './query';

type VariantProperty = NonNullable<ClothingVariantCoreFragment['properties']>[number]['_type'];

function resolveVariant(
	properties: [VariantProperty, string | null | undefined][],
	variants: ClothingVariantCoreFragment[],
) {
	let matching: ClothingVariantCoreFragment[] = [...variants];

	for (const [type, selected] of properties) {
		if (selected) {
			const result = matching.filter((variant) => {
				const property = variant.properties?.find((property) => property._type === type);
				const hasColor =
					property &&
					'resolvedColor' in property &&
					property.resolvedColor?._id == selected;

				const hasSize =
					property &&
					'resolvedSize' in property &&
					property.resolvedSize?._id == selected;

				return hasColor || hasSize;
			});

			if (result.length > 0) {
				matching = [...result];
			}
		}
	}

	return matching.at(0);
}

function resolveVariantProperties(
	properties: [VariantProperty, string | null | undefined][],
	variants: ClothingVariantCoreFragment[],
) {
	const propertiesById = {
		sizeProperty: new Map<string, ProductSizeFragment>(),
		baseColorProperty: new Map<string, ProductColorFragment>(),
		printColorProperty: new Map<string, ProductColorFragment>(),
		contrastColorProperty: new Map<string, ProductColorFragment>(),
	};

	properties.forEach(([type], i) => {
		// find variants matching the previous property if selected (ie. size -> baseColor -> printColor), otherwise use all variants
		const prevSelected = i > 0 ? properties[i - 1][1] : null;
		const prevType = prevSelected ? properties[i - 1][0] : null;

		const matching = prevSelected
			? variants.filter((variant) =>
					variant.properties?.find((property) => {
						const hasSize =
							prevType === 'sizeProperty' &&
							property._type === prevType &&
							property.resolvedSize?._id === prevSelected;
						const hasColor =
							prevType !== 'sizeProperty' &&
							property._type === prevType &&
							property.resolvedColor?._id === prevSelected;
						return hasSize || hasColor;
					}),
				)
			: [...variants];

		for (const variant of matching) {
			for (const property of variant.properties ?? []) {
				if (type === 'sizeProperty') {
					if (property._type === type && property.resolvedSize) {
						propertiesById['sizeProperty'].set(
							property.resolvedSize._id,
							property.resolvedSize,
						);
					}
				} else {
					if (property._type === type && property.resolvedColor) {
						propertiesById[type].set(
							property.resolvedColor._id,
							property.resolvedColor,
						);
					}
				}
			}
		}
	});

	return {
		sizes: Array.from(propertiesById.sizeProperty.values()),
		baseColors: Array.from(propertiesById.baseColorProperty.values()),
		printColors: Array.from(propertiesById.printColorProperty.values()),
		contrastColors: Array.from(propertiesById.contrastColorProperty.values()),
	};
}

/** To eliminate potential errors  */
export const findVariantForSelectedProperties = (
	variants: ClothingVariantCoreFragment[],
	selectedSizeId: string | null,
	selectedBaseColorId: string | null,
	selectedPrintColorId: string | null,
	selectedContrastColorId: string | null,
) => {
	return resolveVariant(
		[
			['sizeProperty', selectedSizeId],
			['baseColorProperty', selectedBaseColorId],
			['printColorProperty', selectedPrintColorId],
			['contrastColorProperty', selectedContrastColorId],
		],
		variants,
	);
};

export const findVariantPropertiesForDefaultProperties = (
	variants: ClothingVariantCoreFragment[],
	defaultSizeId: string | undefined,
	defaultBaseColorId: string | undefined,
	defaultPrintColorId: string | undefined,
	defaultContrastColorId: string | undefined,
) => {
	return resolveVariantProperties(
		[
			['sizeProperty', defaultSizeId],
			['baseColorProperty', defaultBaseColorId],
			['printColorProperty', defaultPrintColorId],
			['contrastColorProperty', defaultContrastColorId],
		],
		variants,
	);
};

/**
 * Returns available properties for the selection.
 * @param properties
 * @param variants
 */
export function findVariantPropertiesForSelectedProperties(
	properties: [VariantProperty, string | null | undefined][],
	variants: ClothingVariantCoreFragment[],
) {
	const propertiesById = {
		sizeProperty: new Map<string, ProductSizeFragment>(),
		baseColorProperty: new Map<string, ProductColorFragment>(),
		printColorProperty: new Map<string, ProductColorFragment>(),
		contrastColorProperty: new Map<string, ProductColorFragment>(),
	};

	properties.forEach(([type], i) => {
		// find variants matching the previous property if selected (ie. size -> baseColor -> printColor), otherwise use all variants
		const prevSelected = i > 0 ? properties[i - 1][1] : null;
		const prevType = prevSelected ? properties[i - 1][0] : null;

		const matching = prevSelected
			? variants.filter((variant) =>
					variant.properties?.find((property) => {
						const hasSize =
							prevType === 'sizeProperty' &&
							property._type === prevType &&
							property.resolvedSize?._id === prevSelected;
						const hasColor =
							prevType !== 'sizeProperty' &&
							property._type === prevType &&
							property.resolvedColor?._id === prevSelected;
						return hasSize || hasColor;
					}),
				)
			: [...variants];

		for (const variant of matching) {
			for (const property of variant.properties ?? []) {
				if (type === 'sizeProperty') {
					if (property._type === type && property.resolvedSize) {
						propertiesById['sizeProperty'].set(
							property.resolvedSize._id,
							property.resolvedSize,
						);
					}
				} else {
					if (property._type === type && property.resolvedColor) {
						propertiesById[type].set(
							property.resolvedColor._id,
							property.resolvedColor,
						);
					}
				}
			}
		}
	});

	return {
		sizes: Array.from(propertiesById.sizeProperty.values()),
		baseColors: Array.from(propertiesById.baseColorProperty.values()),
		printColors: Array.from(propertiesById.printColorProperty.values()),
		contrastColors: Array.from(propertiesById.contrastColorProperty.values()),
	};
}
